import { useEffect, useRef, useState, type ChangeEvent } from "react";
import type { GenerationErrorCode, WorkStep } from "@studio/shared";
import { InlineSpinner } from "../components/InlineSpinner";
import { PictureVideoPanel, usePictureVideo } from "../components/PictureVideoPanel";
import { CharCounter, ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { ApiError, apiRequest, useJobProgress, useWork } from "../lib/api";
import { navigate } from "../lib/navigation";
import { pictureSummary } from "../lib/picture";
import { loadSession, pathForStep } from "../lib/session";

/**
 * 그림 수업 화면 2·3 · 만드는 중 / 그림 완성.
 *
 * 🔑 그림 1장 = 컷 1개(D12). 만들기·말로 고치기·내 그림 업로드는 영상 수업 컷과 **같은 문**
 *    (`/api/cuts/:id/image`·`upload-image`)을 쓰고, 「다시 만들기」만 고른 칸을 다시 보내는
 *    `/api/works/:id/picture` 로 간다(참고 이미지 없이 새로 그린다).
 */

const failureMessages: Record<GenerationErrorCode, string> = {
  PROVIDER_TIMEOUT: "시간이 오래 걸려 멈췄어요. 다시 만들어 주세요.",
  PROVIDER_REJECTED: "그림을 만들지 못했어요. 다시 만들어 주세요.",
  MODERATION_BLOCKED: "안전한 그림으로 고쳐서 다시 만들어 주세요.",
  BUDGET_EXHAUSTED: "오늘 만들 수 있는 그림을 다 썼어요.",
  INTERNAL: "그림을 만들지 못했어요. 다시 만들어 주세요.",
};

function requestError(caught: unknown): string {
  if (!(caught instanceof ApiError)) return "요청을 처리하지 못했어요.";
  if (caught.code === "LIMIT_EXHAUSTED") return "그림 다시 만들기 횟수를 다 썼어요. 선생님께 요청해 주세요.";
  return caught.message;
}

export function PictureImagePage({ workId }: { workId: string }) {
  const query = useWork(workId);
  const cut = query.data?.cuts[0] ?? null;
  const jobId = cut?.current_generation_job_id ?? null;
  const progress = useJobProgress(jobId);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState<"regen" | "edit" | "upload" | "stop" | "next" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [instruction, setInstruction] = useState("");
  const fileInput = useRef<HTMLInputElement | null>(null);
  const handledTerminal = useRef<string | null>(null);
  const refreshRef = useRef(query.refresh);
  refreshRef.current = query.refresh;
  const name = loadSession()?.name ?? "나";

  const imageAssetId = cut?.image_asset_id ?? null;
  // 🔑 2026-10-16 jery: 「그림을 만든 뒤 바로 영상으로」 — 그림이 나온 이 화면에서 바로 만든다.
  const pictureVideo = usePictureVideo(workId, imageAssetId);
  useEffect(() => {
    if (!imageAssetId) {
      setImageUrl(null);
      return undefined;
    }
    let cancelled = false;
    void apiRequest<{ url: string }>(`/api/assets/${encodeURIComponent(imageAssetId)}/url`)
      .then((result) => { if (!cancelled) setImageUrl(result.url); })
      .catch(() => { if (!cancelled) setError("그림을 불러오지 못했어요. 새로고침해 주세요."); });
    return () => { cancelled = true; };
  }, [imageAssetId]);

  useEffect(() => {
    const state = progress.progress?.state;
    if (!state || ["queued", "dispatched", "cancel_requested"].includes(state)) return;
    if (handledTerminal.current === `${jobId}:${state}`) return;
    handledTerminal.current = `${jobId}:${state}`;
    if (state === "failed" || state === "outcome_unknown") {
      setError(failureMessages[progress.progress?.error_code ?? "INTERNAL"]);
    }
    void refreshRef.current();
  }, [jobId, progress.progress?.error_code, progress.progress?.state]);

  // 아직 고르기 전이다(뒤로가기·주소로 들어온 경우) — 고르기 화면으로 보낸다.
  const needsPick = Boolean(query.data && (!cut || !query.data.work.picture));
  useEffect(() => {
    if (needsPick) navigate(pathForStep(workId, "pic_pick"), { replace: true });
  }, [needsPick, workId]);

  if (query.loading) return <LoadingPage label="그림을 불러오는 중..." />;
  if (!query.data || query.error) {
    return <ErrorPage message={query.error?.message ?? "그림을 불러오지 못했습니다."} />;
  }
  const data = query.data;
  const choices = data.work.picture;
  // 위 effect 가 고르기 화면으로 보내는 중이다 — 끝난 실패가 아니라 «이동 대기» 라 스피너가 맞다.
  const redirectPending = !cut || !choices;
  if (redirectPending) return <LoadingPage label="그림 고르기로 가는 중..." />;

  const active = Boolean(jobId && (!progress.progress || ["queued", "dispatched", "cancel_requested"].includes(progress.progress.state)));
  const regenExhausted = Boolean(imageUrl) && data.image_regen_remaining <= 0;
  const locked = active || busy !== null;

  const run = async (kind: NonNullable<typeof busy>, action: () => Promise<void>): Promise<void> => {
    setBusy(kind);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(requestError(caught));
    } finally {
      setBusy(null);
    }
  };

  const regenerate = () => run("regen", async () => {
    await apiRequest(`/api/works/${encodeURIComponent(workId)}/picture`, {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify(choices),
    });
    setEditing(false);
    await query.refresh();
  });

  const edit = () => run("edit", async () => {
    await apiRequest(`/api/cuts/${encodeURIComponent(cut.id)}/image`, {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ mode: "edit", instruction: instruction.trim() }),
    });
    setEditing(false);
    setInstruction("");
    await query.refresh();
  });

  const upload = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await run("upload", async () => {
      const form = new FormData();
      form.set("image", file);
      await apiRequest(`/api/cuts/${encodeURIComponent(cut.id)}/upload-image`, { method: "POST", body: form });
      await query.refresh();
    });
  };

  const stop = () => run("stop", async () => {
    if (!jobId) return;
    await apiRequest(`/api/jobs/${encodeURIComponent(jobId)}/cancel`, { method: "POST", body: JSON.stringify({}) });
    await query.refresh();
  });

  const go = (step: Extract<WorkStep, "pic_song" | "pic_done">) => run("next", async () => {
    await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
      method: "POST",
      body: JSON.stringify({ step, gifts_v: 2 }),
    });
    navigate(pathForStep(workId, step));
  });

  const disabledReason = regenExhausted
    ? "그림 다시 만들기 횟수를 다 썼어요. 내 그림 업로드는 계속 할 수 있어요."
    : null;

  return (
    <StudioShell currentStep={2} imageRegenRemaining={data.image_regen_remaining}>
      <div className="mv-page pic-single">
        <header className="mv-heading">
          <span className="mv-kicker">그림 2 · 그림 완성</span>
          <h1 tabIndex={-1}>{active || !imageUrl ? "그림을 그리고 있어요" : "그림이 나왔어요!"}</h1>
          <p>
            {active || !imageUrl
              ? "잠깐만 기다려요. 곧 나만의 그림이 나와요."
              : "마음에 들면 다음으로 가요. 바꾸고 싶으면 다시 만들어요."}
          </p>
        </header>
        <article className="cut-card pic-square-card">
          <header>
            <div>
              <b>{name}의 그림</b>
              {cut.image_from_upload ? <span className="cut-upload-badge">업로드한 그림</span> : null}
            </div>
            <small>정사각형</small>
          </header>
          <div className="cut-image-stage pic-square">
            {imageUrl ? <img src={imageUrl} alt={`${name}의 그림: ${pictureSummary(choices)}`} /> : null}
            {active ? (
              <div className={`cut-progress${imageUrl ? " over-image" : ""}`}>
                <span />{progress.progress?.state === "queued" ? "차례 기다리는 중" : "그림 만드는 중"}
              </div>
            ) : null}
            {!imageUrl && !active ? <div className="cut-placeholder" aria-hidden="true">?</div> : null}
          </div>
          <p className="cut-scene-text">{pictureSummary(choices)}</p>
          {disabledReason ? <p className="cut-gate" id="pic-disabled-reason">{disabledReason}</p> : null}
          {active ? (
            <button className="button-secondary full-width" type="button" disabled={busy === "stop"} onClick={() => void stop()}>
              {busy === "stop" ? <InlineSpinner label="멈추는 중..." /> : "중지"}
            </button>
          ) : (
            <div className="cut-actions">
              <button
                className={imageUrl ? "button-secondary" : "button-primary"}
                type="button"
                disabled={locked || regenExhausted}
                aria-describedby={disabledReason ? "pic-disabled-reason" : undefined}
                onClick={() => void regenerate()}
              >{busy === "regen" ? <InlineSpinner label="시작하는 중..." /> : imageUrl ? "다시 만들기" : "그림 만들기"}</button>
              {imageUrl ? (
                <button
                  className="button-secondary"
                  type="button"
                  disabled={locked || regenExhausted}
                  aria-describedby={disabledReason ? "pic-disabled-reason" : undefined}
                  onClick={() => setEditing((value) => !value)}
                >말로 고치기</button>
              ) : null}
              <button
                className="button-secondary"
                type="button"
                disabled={locked}
                aria-label="내 그림 업로드 (PNG·JPG·WEBP, 10MB 이하)"
                onClick={() => fileInput.current?.click()}
              >{busy === "upload" ? <InlineSpinner label="올리는 중..." /> : "내 그림 업로드"}</button>
              <input ref={fileInput} className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => void upload(event)} />
            </div>
          )}
          {editing && !active ? (
            <div className="cut-edit-box">
              <label htmlFor="pic-edit">어떻게 고칠까요?</label>
              <textarea id="pic-edit" value={instruction} maxLength={600} placeholder="예: 하늘을 보라색 노을로 바꿔 줘" onChange={(event) => setInstruction(event.target.value)} />
              <CharCounter value={instruction} max={600} />
              <button className="button-primary" type="button" disabled={!instruction.trim() || locked || regenExhausted} onClick={() => void edit()}>
                {busy === "edit" ? <InlineSpinner label="시작하는 중..." /> : "고치기"}
              </button>
            </div>
          ) : null}
        </article>
        {imageUrl && !active ? <PictureVideoPanel workId={workId} video={pictureVideo} disabled={locked} /> : null}
        {error ? <InlineError>{error}</InlineError> : null}
        <div className="pic-branch">
          <button className="button-secondary" type="button" disabled={!imageUrl || locked} onClick={() => void go("pic_done")}>
            노래 없이 완성
          </button>
          <button className="button-primary" type="button" disabled={!imageUrl || locked} onClick={() => void go("pic_song")}>
            {busy === "next" ? <InlineSpinner label="저장 중..." /> : "노래 붙이기 →"}
          </button>
        </div>
        <p className="mv-next-stage-note pic-center">노래는 음악 만들기 수업에서 만든 곡 코드나 음원 파일로 붙일 수 있어요.</p>
        <p className="pic-center">
          <button className="button-ghost" type="button" disabled={locked} onClick={() => navigate(pathForStep(workId, "pic_pick"))}>
            ← 고른 것 바꾸기
          </button>
        </p>
      </div>
    </StudioShell>
  );
}
